import { computed, DestroyRef, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { GetHomeRequest, Home, Home_SpecialEffect } from '@proto/store/api/store/home';
import { BaseViewModel } from '../base-view-model';
import { SearchBarModel } from '@common/search-bar/model';
import { SlideItem } from '@common/swiper/model';
import { HomeRepositoryService } from '@service/home-repository.service';
import { MiniHomeSort } from '@helper/mini-home';
import { Constants } from '@helper/constants';
import { HomeHeaderModel } from '@common/home-header/home-header.model';
import { MetaData } from '@helper/meta-data';

import { isEqual } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class HomeModel extends BaseViewModel<Home> {
  readonly previewDisplayCount = 100;
  readonly banners = computed(
    () =>
      this.apiResult()
        .banner?.banners.filter(p => !!p.hash)
        .map(i => new SlideItem(i.hash, i.id, i.link)) ?? [],
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly waterfall = computed(
    () => this.apiResult().waterfall?.columns.map(c => c.items.slice(0, this.itemCount())) ?? [],
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly sortBy = signal(MiniHomeSort.NORMAL);
  // use for displaying price sorting button
  readonly sortPrice = signal(MiniHomeSort.PRICE);
  readonly itemCount = signal(Constants.displayCount(this.detectService.isMobileDevice()));
  readonly snowEffect = signal(Home_SpecialEffect.NONE);

  constructor(
    private lativRepository: HomeRepositoryService,
    private homeHeaderViewModel: HomeHeaderModel,
    private searchBar: SearchBarModel,
    private route: ActivatedRoute
  ) {
    super(Home.fromPartial({}));
  }

  getHome(ref: DestroyRef, waterfallId = ''): void {
    const request = GetHomeRequest.fromPartial({});
    if (this.route.snapshot.queryParams['id']) {
      request.id = this.route.snapshot.queryParams['id'];
      this.itemCount.set(this.previewDisplayCount);
    } else if (waterfallId) {
      request.previewId = waterfallId ?? '';
    }
    this.setApiResult(Home.fromPartial({}));
    this.lativRepository
      .getHome(request)
      .pipe(takeUntilDestroyed(ref))
      .subscribe({
        next: home => {
          this.setApiResult(home);
          this.searchBar.searchText.set(home.searchKeyword);
          this.homeHeaderViewModel.setAnnouncement(home.topAnnouncement?.id ?? '', home.topAnnouncement?.content ?? '');
          this.snowEffect.set(home.specialEffect);
        },
        error: err => this.setApiError(err),
      });
  }

  override getMetaByUrl(segments: string[], model: Home): MetaData | undefined {
    if (segments.length > 0) {
      return undefined;
    }
    return new MetaData('', '', model.banner?.banners[0]?.hash);
  }
}
